import React, { useEffect, useMemo, useState } from 'react';
import { Ticket, TransferLogEntry } from '../types';
import { Button, Card, Flex, Input, Text } from './base-components';
import { useToast } from '../contexts/ToastContext';
import { useBatchTransfer, useCheckDuplicateTransfer } from '../hooks/useApi';
import { trackEvent } from '../services/analytics';
import {
  BatchTransferPlatform,
  isBatchTransferable,
  ticketTypeLabel,
  ticketShortId,
  normalizePhone,
  isPhoneValid,
  eventNameOf,
  eventDateOf,
  externalEventIdOf,
} from './transferUtils';

type Step = 'account' | 'event' | 'transfer';

interface AccountBucket {
  key: string;
  connectionId: number | null;
  label: string;
  platform: string;
  count: number;
  tickets: Ticket[];
}

interface EventBucket {
  key: string;
  name: string;
  date: string | null;
  externalEventId: string | null;
  tickets: Ticket[];
}

interface PerAccountResult {
  account_label: string;
  ticket_ids: string[];
  success: boolean;
  tickets_transferred?: number;
  error?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  tickets: Ticket[];
}

const accountBucketLabel = (t: Ticket): { label: string; key: string; connectionId: number | null; platform: string } => {
  const conn = t.external_platform_connection;
  if (!conn) return { label: 'Unknown account', key: 'none', connectionId: null, platform: t.platform || 'UNKNOWN' };
  const label = conn.phone
    ? `${conn.platform} ${conn.phone}`
    : conn.email
      ? `${conn.platform} ${conn.email}`
      : `${conn.platform} (#${conn.id})`;
  return { label, key: `c${conn.id}`, connectionId: conn.id, platform: conn.platform };
};

const formatEventDate = (iso: string | null): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const MultiTransferModal: React.FC<Props> = ({ open, onClose, tickets }) => {
  const { showSuccess, showError } = useToast();
  const batch = useBatchTransfer();
  const dupCheck = useCheckDuplicateTransfer();

  const transferable = useMemo(() => tickets.filter(isBatchTransferable), [tickets]);

  // Step 1: account buckets across all transferable tickets.
  const accountBuckets: AccountBucket[] = useMemo(() => {
    const byKey = new Map<string, AccountBucket>();
    transferable.forEach((t) => {
      const { label, key, connectionId, platform } = accountBucketLabel(t);
      if (!byKey.has(key)) {
        byKey.set(key, { key, connectionId, label, platform, count: 0, tickets: [] });
      }
      const b = byKey.get(key)!;
      b.tickets.push(t);
      b.count++;
    });
    return Array.from(byKey.values()).sort((a, b) => b.count - a.count);
  }, [transferable]);

  const [step, setStep] = useState<Step>('account');
  const [accountKey, setAccountKey] = useState<string | null>(null);
  const [eventKey, setEventKey] = useState<string | null>(null);

  // Step 2: events available on the chosen account.
  const eventBuckets: EventBucket[] = useMemo(() => {
    const account = accountBuckets.find((a) => a.key === accountKey);
    if (!account) return [];
    const byKey = new Map<string, EventBucket>();
    account.tickets.forEach((t) => {
      const name = eventNameOf(t);
      const date = eventDateOf(t);
      const extId = externalEventIdOf(t, account.platform);
      const key = `${name}__${date || ''}`;
      if (!byKey.has(key)) {
        byKey.set(key, { key, name, date, externalEventId: extId, tickets: [] });
      }
      const b = byKey.get(key)!;
      b.tickets.push(t);
      if (extId && !b.externalEventId) b.externalEventId = extId;
    });
    return Array.from(byKey.values()).sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  }, [accountBuckets, accountKey]);

  // Step 3 state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [phone, setPhone] = useState('');
  const [results, setResults] = useState<PerAccountResult[] | null>(null);
  const [strictMatches, setStrictMatches] = useState<TransferLogEntry[] | null>(null);
  const [looseMatches, setLooseMatches] = useState<TransferLogEntry[]>([]);
  const [confirmAnyway, setConfirmAnyway] = useState(false);

  // Reset on open and auto-skip steps when only one option exists.
  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setPhone('');
    setResults(null);
    setStrictMatches(null);
    setLooseMatches([]);
    setConfirmAnyway(false);

    if (accountBuckets.length === 0) {
      setStep('account');
      setAccountKey(null);
      setEventKey(null);
      return;
    }
    if (accountBuckets.length === 1) {
      setAccountKey(accountBuckets[0].key);
      // Defer event auto-skip to the second effect once accountKey/eventBuckets settle.
      setStep('event');
    } else {
      setAccountKey(null);
      setEventKey(null);
      setStep('account');
    }
  }, [open, accountBuckets]);

  // After accountKey is set, auto-skip event step if only one event remains.
  useEffect(() => {
    if (!open) return;
    if (step !== 'event') return;
    if (eventBuckets.length === 1) {
      setEventKey(eventBuckets[0].key);
      setStep('transfer');
    }
  }, [open, step, eventBuckets]);

  // Reset duplicate-check state when selection or phone changes.
  useEffect(() => {
    setStrictMatches(null);
    setLooseMatches([]);
    setConfirmAnyway(false);
  }, [phone, selectedIds]);

  const chosenAccount = accountBuckets.find((a) => a.key === accountKey) || null;
  const chosenEvent = eventBuckets.find((e) => e.key === eventKey) || null;

  const stepBackTarget = (): Step | null => {
    if (step === 'event' && accountBuckets.length > 1) return 'account';
    if (step === 'transfer') {
      if (eventBuckets.length > 1) return 'event';
      if (accountBuckets.length > 1) return 'account';
    }
    return null;
  };

  const goBack = () => {
    const target = stepBackTarget();
    if (!target) return;
    if (target === 'account') {
      setAccountKey(null);
      setEventKey(null);
    } else if (target === 'event') {
      setEventKey(null);
    }
    setSelectedIds(new Set());
    setStep(target);
  };

  // Step-3 derived state
  const totalSelected = selectedIds.size;
  const phoneInvalid = phone.length > 0 && !isPhoneValid(phone);
  const canSubmit =
    !batch.isPending &&
    totalSelected > 0 &&
    isPhoneValid(phone);

  const toggleTicket = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (!chosenEvent) return;
    const allIds = chosenEvent.tickets.map((t) => t.external_ticket_id);
    const allSelected = allIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      allIds.forEach((id) => {
        if (allSelected) next.delete(id);
        else next.add(id);
      });
      return next;
    });
  };

  const handleSubmit = async (bypassDupCheck = false) => {
    if (!canSubmit || !chosenAccount || !chosenEvent) return;

    const selectedTickets = chosenEvent.tickets.filter((t) => selectedIds.has(t.external_ticket_id));
    const ticket_ids = selectedTickets.map((t) => t.external_ticket_id);
    const event_id = chosenEvent.externalEventId || '';

    const platform = chosenAccount.platform.toUpperCase() as BatchTransferPlatform;

    if (!event_id) {
      showError(`Could not resolve ${platform} event id for the selected event.`);
      return;
    }

    if (!bypassDupCheck && !confirmAnyway) {
      try {
        const dup = await dupCheck.mutateAsync({
          event_id,
          recipient_phone: normalizePhone(phone),
          ticket_ids,
        });
        if (dup.strict_matches.length > 0 || dup.loose_matches.length > 0) {
          setStrictMatches(dup.strict_matches);
          setLooseMatches(dup.loose_matches);
          if (dup.strict_matches.length > 0) return;
        }
      } catch {
        // Soft-fail: proceed with submit.
      }
    }

    try {
      trackEvent('multi_transfer_submitted', {
        accountId: String(chosenAccount.connectionId ?? ''),
        eventName: chosenEvent.name,
        ticketCount: ticket_ids.length,
      });
      const res = await batch.mutateAsync({
        platform,
        transfers: [{
          event_id,
          recipient_phone: normalizePhone(phone),
          ticket_ids,
          client_ref: chosenAccount.key,
        }],
      });
      const perAccount: PerAccountResult[] = res.results.map((r) => ({
        account_label: chosenAccount.label,
        ticket_ids: r.ticket_ids,
        success: r.success,
        tickets_transferred: r.tickets_transferred,
        error: r.error,
      }));
      setResults(perAccount);
      const { succeeded, failed, tickets_transferred } = res.summary;
      if (failed === 0) {
        showSuccess(`Transferred ${tickets_transferred} ticket${tickets_transferred === 1 ? '' : 's'}`);
      } else if (succeeded === 0) {
        showError('Transfer failed');
      } else {
        showError(`Sent ${tickets_transferred}, ${failed} failed`);
      }
    } catch (e: any) {
      showError(e?.message || 'Transfer failed');
    }
  };

  if (!open) return null;

  const headerTitle =
    step === 'account' ? 'Pick an account' :
    step === 'event' ? 'Pick an event' :
    'Send tickets';
  const headerSubtitle =
    step === 'account' ? `${accountBuckets.length} account${accountBuckets.length === 1 ? '' : 's'} with transferable tickets` :
    step === 'event' ? (chosenAccount ? chosenAccount.label : '') :
    chosenEvent ? `${chosenEvent.name}${chosenEvent.date ? ` · ${formatEventDate(chosenEvent.date)}` : ''}` : '';

  const allSucceeded = results !== null && results.length > 0 && results.every((r) => r.success);

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="lg"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          {stepBackTarget() && !results && (
            <button
              type="button"
              onClick={goBack}
              className="mt-1 p-1 rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Back"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div className="flex-1 min-w-0">
            <Text variant="heading" mb={1}>{headerTitle}</Text>
            {headerSubtitle && <Text variant="muted" size="sm">{headerSubtitle}</Text>}
          </div>
          <div className="flex items-center gap-1 mt-2">
            {(['account', 'event', 'transfer'] as Step[]).map((s, i) => {
              const isActive = step === s;
              const isPast = (['account', 'event', 'transfer'] as Step[]).indexOf(step) > i;
              return (
                <span
                  key={s}
                  className={`w-6 h-1 rounded-full ${
                    isActive ? 'bg-blue-500' : isPast ? 'bg-white/40' : 'bg-white/10'
                  }`}
                />
              );
            })}
          </div>
        </div>

        {/* No transferable tickets at all */}
        {accountBuckets.length === 0 && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-amber-400 text-sm">
              No transferable DICE or TAO tickets across your connected accounts.
            </p>
          </div>
        )}

        {/* Step 1 — account picker */}
        {!results && step === 'account' && accountBuckets.length > 0 && (
          <>
            <div className="space-y-2 mb-4 max-h-[55vh] overflow-y-auto pr-1">
              {accountBuckets.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => { setAccountKey(a.key); setStep('event'); }}
                  className="w-full text-left p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all flex items-center justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/5 text-white/70 border border-white/10 font-medium">
                        {a.platform}
                      </span>
                      <span className="text-sm text-white font-medium truncate">{a.label}</span>
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                      {a.count} transferable ticket{a.count === 1 ? '' : 's'}
                    </p>
                  </div>
                  <svg className="w-4 h-4 text-white/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
            <Flex justify="end" gap={2}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
            </Flex>
          </>
        )}

        {/* Step 2 — event picker */}
        {!results && step === 'event' && (
          <>
            {eventBuckets.length === 0 ? (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-amber-400 text-sm">No events with transferable tickets on this account.</p>
              </div>
            ) : (
              <div className="space-y-2 mb-4 max-h-[55vh] overflow-y-auto pr-1">
                {eventBuckets.map((e) => (
                  <button
                    key={e.key}
                    type="button"
                    onClick={() => { setEventKey(e.key); setStep('transfer'); }}
                    disabled={!e.externalEventId}
                    title={!e.externalEventId ? 'Missing external event id — cannot transfer' : undefined}
                    className="w-full text-left p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all flex items-center justify-between gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">{e.name}</p>
                      <p className="text-xs text-white/40 mt-0.5">
                        {e.date ? formatEventDate(e.date) : 'No date'} · {e.tickets.length} ticket{e.tickets.length === 1 ? '' : 's'}
                      </p>
                    </div>
                    <svg className="w-4 h-4 text-white/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ))}
              </div>
            )}
            <Flex justify="end" gap={2}>
              <Button variant="secondary" onClick={onClose}>Cancel</Button>
            </Flex>
          </>
        )}

        {/* Step 3 — ticket grid + recipient + send */}
        {!results && step === 'transfer' && chosenAccount && chosenEvent && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm text-white/60">
                {totalSelected > 0 ? (
                  <><span className="text-white font-medium">{totalSelected}</span> selected</>
                ) : (
                  <span className="text-white/40">Select tickets to transfer</span>
                )}
              </span>
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-white/50 hover:text-white transition-colors"
              >
                {chosenEvent.tickets.every((t) => selectedIds.has(t.external_ticket_id)) ? 'Deselect all' : 'Select all'}
              </button>
            </div>

            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] overflow-hidden mb-4 max-h-[35vh] overflow-y-auto">
              <div className="divide-y divide-white/[0.04]">
                {chosenEvent.tickets.map((t, i) => {
                  const checked = selectedIds.has(t.external_ticket_id);
                  return (
                    <label
                      key={t.external_ticket_id}
                      className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                        checked ? 'bg-blue-500/[0.06]' : 'hover:bg-white/[0.02]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTicket(t.external_ticket_id)}
                        className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/50 focus:ring-offset-0"
                      />
                      <span className="text-xs text-white/40 tabular-nums w-6">#{i + 1}</span>
                      <span className="text-sm text-white/80">{ticketTypeLabel(t)}</span>
                      <span className="text-xs text-white/30 ml-auto font-mono">{ticketShortId(t)}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <Input
                type="tel"
                label="Recipient phone (with country code)"
                placeholder="+15551234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={phoneInvalid}
                errorMessage={phoneInvalid ? 'e.g. +15551234567' : undefined}
              />
            </div>

            {strictMatches && strictMatches.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.732-3L13.732 4a2 2 0 00-3.464 0L3.268 16A2 2 0 005 19z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-red-400">
                      You already sent {strictMatches.length === 1 ? 'this' : 'some of these'} ticket{strictMatches.length === 1 && (strictMatches[0].overlapping_ticket_ids?.length || 0) <= 1 ? '' : 's'} to this number.
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {strictMatches.slice(0, 3).map((m) => (
                        <li key={m.id} className="text-xs text-red-300/80">
                          {(m.overlapping_ticket_ids?.length || 0)} ticket{(m.overlapping_ticket_ids?.length || 0) === 1 ? '' : 's'}
                          {' '}sent {new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          {m.account?.phone_masked && ` from ${m.account.phone_masked}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {looseMatches.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-400">
                  You sent <span className="font-medium">{looseMatches.length}</span> other transfer{looseMatches.length === 1 ? '' : 's'} for this event to this number recently.
                </p>
              </div>
            )}

            <Flex justify="end" gap={2}>
              <Button variant="secondary" onClick={onClose} disabled={batch.isPending || dupCheck.isPending}>
                Cancel
              </Button>
              {strictMatches && strictMatches.length > 0 && !confirmAnyway ? (
                <Button
                  variant="primary"
                  onClick={() => { setConfirmAnyway(true); handleSubmit(true); }}
                  disabled={batch.isPending || dupCheck.isPending}
                >
                  Send anyway
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => handleSubmit()}
                  disabled={!canSubmit || dupCheck.isPending}
                >
                  {batch.isPending
                    ? 'Sending…'
                    : dupCheck.isPending
                      ? 'Checking…'
                      : `Send ${totalSelected} ticket${totalSelected === 1 ? '' : 's'}`}
                </Button>
              )}
            </Flex>
          </>
        )}

        {/* Result step (replaces all when results exist) */}
        {results && (
          <>
            <div className="space-y-2 mb-4 max-h-[50vh] overflow-y-auto pr-1">
              {results.map((r, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    r.success
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white">{r.account_label}</span>
                    <span className={`text-xs font-medium ${r.success ? 'text-emerald-400' : 'text-red-400'}`}>
                      {r.success
                        ? `${r.tickets_transferred ?? r.ticket_ids.length} sent`
                        : 'Failed'}
                    </span>
                  </div>
                  {!r.success && r.error && (
                    <p className="text-xs text-red-400/90 mt-1">{r.error}</p>
                  )}
                </div>
              ))}
            </div>
            <Flex justify="end" gap={2}>
              <Button variant="primary" onClick={onClose}>
                {allSucceeded ? 'Done' : 'Close'}
              </Button>
            </Flex>
          </>
        )}
      </Card>
    </Flex>
  );
};
